import {
  GLOBAL_EVENT_TARGET,
  getData,
  isElementCollection,
  triggerCancelableCustomEvent,
  triggerCustomEvent,
  triggerGlobalCustomEvent,
} from './utils/dom';
import { toArray } from './utils/js';
import ListenerEvent from './ListenerEvent';

/**
 * Class to extend for event listener methods. Provides `on`, `off` and
 * `triggerCustomEvent`.
 */
export default class EventManager {
  /**
   * Custom event listeners.
   *
   * @see _addListener()
   */
  _listeners = new WeakMap();

  /**
   * Bind an event listener to an element.
   *
   * The listeners supplied to `on` are wrapped in `_bind` and `_delegate` to
   * enable the custom event object and `event.detail` for custom events.
   * Because of this the actual listener on the node is not the same function
   * that's passed to `on` and thus can't be removed with removeEventListener.
   * To make this work, a map of original listeners -> custom data is kept for
   * `_removeListener`.
   *
   * @param {Element} el - DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {Function} suppliedListener - The listener supplied to `on`.
   * @param {Function} customListener - The custom listener created in `_bind`
   *   or `_delegate`.
   * @param {boolean} [useCapture] - The third addEventListener param.
   */
  _addListener(
    el,
    eventName,
    suppliedListener,
    customListener,
    useCapture = false,
  ) {
    const current = this._listeners.get(suppliedListener) || [];
    this._listeners.set(suppliedListener, [...current, [el, customListener]]);
    el.addEventListener(eventName, customListener, useCapture);
  }

  /**
   * Remove an event listener from an element.
   *
   * @see _addListener()
   * @param {Element} el - DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {Function} suppliedListener - The listener supplied to `on`.
   * @param {boolean} [useCapture] - The third removeEventListener param, must
   *   be the same as supplied to addEventListener.
   */
  _removeListener(el, eventName, suppliedListener, useCapture = false) {
    // Get the listener for this specific element since the same listener
    // can be bound to multiple elements.
    const listenerData = (this._listeners.get(suppliedListener) || []).find(
      ([listenerEl]) => listenerEl === el,
    );
    const listener = listenerData ? listenerData[1] : suppliedListener;
    el.removeEventListener(eventName, listener, useCapture);
  }

  /**
   * Get spreadable extra parameters for event listeners.
   *
   * @see _bind()
   * @see _delegate()
   * @param {?object} event - Event instance.
   * @returns {Array}
   */
  _getListenerParams(event) {
    return event?.detail ? toArray(event.detail) : [];
  }

  /**
   * Bind a regular event listener to an element.
   *
   * @param {Element} el - The DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {Function} listener - Event listener.
   * @param {boolean} [useCapture] - If the listener should trigger on the
   *   capture phase, defaults to false.
   */
  _bind(el, eventName, listener, useCapture = false) {
    const customListener = (event) => {
      const extraParams = this._getListenerParams(event);
      listener(new ListenerEvent(event), ...extraParams);
    };
    this._addListener(el, eventName, listener, customListener, useCapture);
  }

  /**
   * Bind an event listener that delegates to the specified selector.
   *
   * @param {Element} el - The DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {string} delegate - Selector to delegate to.
   * @param {Function} listener - Event listener.
   * @param {boolean} [useCapture] - If the listener should trigger on the
   *   capture phase, defaults to false.
   */
  _delegate(el, eventName, delegate, listener, useCapture = false) {
    const customListener = (event) => {
      const { currentTarget } = event;
      let { target } = event;
      while (target) {
        if (
          // Only check element nodes
          target.nodeType === 1 &&
          (target.matches(delegate) ||
            (getData(target, 'node-name') || '').split(' ').includes(delegate))
        ) {
          const extraParams = this._getListenerParams(event);
          listener(new ListenerEvent(event, target), ...extraParams);
          return;
        }
        target =
          target !== currentTarget && target !== document.documentElement
            ? target.parentNode
            : null;
      }
    };
    this._addListener(el, eventName, listener, customListener, useCapture);
  }

  /**
   * Bind an event, either directly or with delegation to a selector.
   *
   * If passing an array or a NodeList, the event will be bound to each Node.
   * Depending on the use case, consider switching to the delegation model
   * instead.
   *
   * @param {Element|NodeList|Array.<Element>} el - The DOM node(s) to bind the
   *   event on.
   * @param {string} eventName - Name of the event ('click', 'change'...).
   * @param {string|Function} delegateOrListener - Selector to delegate to, or
   *   event listener when not using delegation.
   * @param {Function} [listener] - Event listener when using delegation.
   * @param {boolean} [useCapture=false] - If the listener should trigger on the
   *   capture phase, defaults to false.
   * @returns {Element} The passed in DOM node(s).
   * @example
   *
   * // Bind a click handler to a <button>.
   * on(buttonNode, 'click', (e) => {...});
   *
   * @example
   *
   * // Bind a click handler to a <nav> that is triggered whenever an element
   * // with the .nav-link class inside it is clicked.
   * on(navNode, 'click', '.nav-link', (e) => {...});
   *
   * @example
   *
   * // Bind a focus handler to a node using the capture phase.
   * on(someNode, 'focus', null, (e) => {...}, true);
   */
  on(el, eventName, delegateOrListener, listener, useCapture = false) {
    // Validate signature when not in prod.
    if (process.env.NODE_ENV !== 'production') {
      if (!eventName || typeof eventName !== 'string') {
        throw new TypeError('The event name must be a string');
      }
      if (!el || typeof el !== 'object') {
        throw new TypeError(`${eventName} event must be bound to a DOM node`);
      }
      // Regular binding version
      if (!listener && typeof delegateOrListener !== 'function') {
        throw new TypeError(
          `The ${eventName} event listener must be a function`,
        );
      }
      // Delegation version
      if (
        listener &&
        (typeof delegateOrListener !== 'string' ||
          typeof listener !== 'function')
      ) {
        throw new TypeError(
          `Delegation for ${eventName} event must pass a selector followed by a listener function`,
        );
      }
    }

    if (isElementCollection(el)) {
      Array.from(el).forEach((collectionEl) => {
        this.on(
          collectionEl,
          eventName,
          delegateOrListener,
          listener,
          useCapture,
        );
      });
      return el;
    }

    if (typeof delegateOrListener === 'function') {
      this._bind(el, eventName, delegateOrListener, useCapture);
    } else {
      this._delegate(el, eventName, delegateOrListener, listener, useCapture);
    }
    return el;
  }

  /**
   * Bind a global event, either directly or with delegation to a selector.
   *
   * @see on()
   *
   * @param {string} eventName - Name of the event ('click', 'change'...).
   * @param {string|Function} delegateOrListener - Selector to delegate to, or
   *   event listener when not using delegation.
   * @param {Function} listener - Event listener when using delegation.
   * @param {boolean} [useCapture] - If the listener should trigger on the
   *   capture phase, defaults to false.
   */
  onGlobal(eventName, delegateOrListener, listener, useCapture = false) {
    this.on(
      GLOBAL_EVENT_TARGET,
      eventName,
      delegateOrListener,
      listener,
      useCapture,
    );
  }

  /**
   * Remove an event listener from an element.
   *
   * @param {Element} el - The DOM node.
   * @param {string} eventName - Name of the event to remove.
   * @param {Function} listener - Event listener that was attached with `on`
   *   (with or without delegation).
   * @param {boolean} [useCapture] - If the listener was bound to the capture
   *   phase, defaults to false.
   * @returns {Element} The passed in DOM node.
   */
  off(el, eventName, listener, useCapture = false) {
    // Validate signature when not in prod.
    if (process.env.NODE_ENV !== 'production') {
      if (!eventName || typeof eventName !== 'string') {
        throw new TypeError('The event name must be a string');
      }
      if (!el || typeof el !== 'object') {
        throw new TypeError(`${eventName} event must be bound to a DOM node`);
      }
      if (typeof listener === 'string') {
        throw new TypeError(
          `A delegate selector ('${listener}') should not be supplied when removing an event`,
        );
      }
      if (typeof listener !== 'function') {
        throw new TypeError(
          `The ${eventName} event listener must be a function`,
        );
      }
    }

    if (isElementCollection(el)) {
      Array.from(el).forEach((collectionEl) => {
        this.off(collectionEl, eventName, listener, useCapture);
      });
      return el;
    }

    this._removeListener(el, eventName, listener, useCapture);
    return el;
  }

  /**
   * Remove an event listener from an element.
   *
   * @see off()
   * @param {string} eventName - Name of the event to remove.
   * @param {Function} listener - Event listener that was attached with `on`.
   * @param {boolean} [useCapture] - If the listener was bound to the capture
   *   phase, defaults to false.
   */
  offGlobal(eventName, listener, useCapture = false) {
    this.off(GLOBAL_EVENT_TARGET, eventName, listener, useCapture);
  }

  /**
   * Trigger a custom event.
   *
   * @param {Element} target - DOM node to trigger the event on.
   * @param {string} eventName - Name of the event to trigger.
   * @param {...*} [extraData] - Any extra data to pass to the listener.
   * @example
   *
   * triggerCustomEvent(document, 'modalOpen', modalData)
   */
  triggerCustomEvent(target, eventName, ...extraData) {
    triggerCustomEvent(target, eventName, ...extraData);
  }

  /**
   * Trigger a custom event that can be cancelled.
   *
   * @param {Element} target - DOM node to trigger the event on.
   * @param {string} eventName - Name of the event to trigger.
   * @param {...*} [extraData] - Any extra data to pass to the listener.
   * @returns {boolean} True if the event's preventDefault has not been
   *   invoked, otherwise false.
   * @example
   *
   * triggerCancelableCustomEvent(document, 'modalOpen', modalData)
   */
  triggerCancelableCustomEvent(target, eventName, ...extraData) {
    return triggerCancelableCustomEvent(target, eventName, ...extraData);
  }

  /**
   * Trigger a global custom event.
   *
   * @param {string} eventName - Name of the event to trigger.
   * @param {...*} [extraData] - Any extra data to pass to the listener.
   * @example
   *
   * triggerGlobalCustomEvent('modalOpen', modalData)
   */
  triggerGlobalCustomEvent(eventName, ...extraData) {
    triggerGlobalCustomEvent(eventName, ...extraData);
  }
}
